import { useCallback, useEffect, useState } from "react";
import { PAYMENT_METHODS } from "@sakani/contracts";
import { api, type OutboxRow, type PayoutRow, type RefundRow } from "../api.ts";
import { day, METHOD, money, wa, digits } from "../format.ts";
import { Act, Empty } from "../ui.tsx";

function Payout({ p, done }: { p: PayoutRow; done: () => void }) {
  const [m, setM] = useState<string>("bank_transfer");
  const [ref, setRef] = useState("");
  return (
    <li className="row">
      <div className="main"><span className="code num">{p.bookingCode}</span><b>{p.host?.name ?? "—"}</b><span className="muted small num">{p.host?.phone ? `+${digits(p.host.phone)}` : "pas de téléphone"} · dû depuis le {day(p.dueAt.slice(0, 10))}</span></div>
      <div className="side"><b className="num">{money(p.amountMinor, p.currency)}</b></div>
      <div className="quick pay">
        <select value={m} onChange={(e) => setM(e.target.value)} aria-label="Moyen">{PAYMENT_METHODS.filter((x) => x !== "card").map((x) => <option key={x} value={x}>{METHOD[x]}</option>)}</select>
        <input placeholder="Référence" value={ref} onChange={(e) => setRef(e.target.value)} />
        <Act run={async () => { await api(`/v1/staff/payouts/${p.id}/paid`, { body: { method: m, reference: ref || undefined } }); done(); }}>Versé</Act>
      </div>
    </li>
  );
}

export function Payouts() {
  const [rows, setRows] = useState<PayoutRow[] | null>(null);
  const load = useCallback(() => { api<PayoutRow[]>("/v1/staff/payouts").then(setRows).catch(() => setRows([])); }, []);
  useEffect(load, [load]);
  return (
    <section>
      <header className="pagehead"><h1>Versements aux propriétaires</h1><p className="muted small">Séjours terminés : le montant total moins les 10 % de Sekani.</p></header>
      {rows && rows.length === 0 && <Empty>Aucun versement en attente.</Empty>}
      <ul className="rows">{rows?.map((p) => <Payout key={p.id} p={p} done={load} />)}</ul>
    </section>
  );
}

export function Refunds() {
  const [rows, setRows] = useState<RefundRow[] | null>(null);
  const load = useCallback(() => { api<RefundRow[]>("/v1/staff/refunds").then(setRows).catch(() => setRows([])); }, []);
  useEffect(load, [load]);
  return (
    <section>
      <header className="pagehead"><h1>Remboursements</h1><p className="muted small">À renvoyer aux clients (Bankily, Sadad, virement…), puis marquer envoyé.</p></header>
      {rows && rows.length === 0 && <Empty>Aucun remboursement en attente.</Empty>}
      <ul className="rows">
        {rows?.map((r) => (
          <li key={r.id} className="row">
            <div className="main"><span className="code num">{r.bookingCode}</span><b>{r.guest?.name ?? "—"}</b><span className="muted small num">{r.guest?.phone ? `+${digits(r.guest.phone)}` : "pas de téléphone"}</span></div>
            <div className="side"><b className="num">{money(r.amountMinor, r.currency)}</b></div>
            <div className="quick">
              {r.guest?.phone && <a className="chip" href={wa(r.guest.phone, `Bonjour ${r.guest.name}, votre remboursement de ${money(r.amountMinor, r.currency)} (réf. ${r.bookingCode}) vient d'être envoyé.`) ?? "#"} target="_blank" rel="noreferrer">WhatsApp</a>}
              <Act run={async () => { await api(`/v1/staff/payments/${r.id}/refund-sent`, { body: {} }); load(); }}>Envoyé</Act>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

const LABEL: Record<string, string> = { ops_new_request: "Nouvelle demande", ops_payment_submitted: "Preuve de paiement", host_availability_check: "Disponibilité à vérifier (hôte)", booking_accepted: "Disponible — paiement", booking_confirmed: "Séjour confirmé", booking_declined: "Indisponible", payment_submitted: "Preuve reçue", booking_cancelled: "Annulation" };

export function Messages() {
  const [rows, setRows] = useState<OutboxRow[] | null>(null);
  const load = useCallback(() => { api<OutboxRow[]>("/v1/staff/notifications/manual").then(setRows).catch(() => setRows([])); }, []);
  useEffect(() => { load(); const i = setInterval(load, 20_000); return () => clearInterval(i); }, [load]);
  return (
    <section>
      <header className="pagehead"><h1>Messages à envoyer</h1><p className="muted small">Rédigés par le système. Ouvrez WhatsApp, envoyez, puis marquez comme envoyé.</p></header>
      {rows && rows.length === 0 && <Empty>Aucun message en attente.</Empty>}
      <ul className="rows">
        {rows?.map((r) => (
          <li key={r.id} className="row">
            <div className="main"><b>{LABEL[r.template] ?? r.template}</b><span className="muted small num">{String(r.payload.code ?? "")} {String(r.payload.listing ?? "")}</span></div>
            <div className="quick">
              {r.manualLink && <a className="chip" href={r.manualLink} target="_blank" rel="noreferrer">Ouvrir WhatsApp</a>}
              <Act tone="ghost" run={async () => { await api(`/v1/staff/notifications/${r.id}/sent`, { body: {} }); load(); }}>Envoyé</Act>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
